import { Stack, Text, useMantineTheme } from '@mantine/core'
import { IconExclamationCircle } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { parseEther } from 'viem'
import { normalize } from 'viem/ens'

import TransactionSummary from '@/components/Transfer/TransactionSummary'
import { CONTRACT_COLLECTOR_ADDRESS } from '@/constants/blockchain'
import L1PaymentCollector from '@/contracts/L1NSPaymentCollector'
import { ContractCall, useChainFunctions } from '@/hooks/useChain'
import useL1NS from '@/hooks/useL1NS'
import useLocalStorage from '@/hooks/useLocalStorage'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'

import { UserIdentity } from '../../../utils/utils'
import RootButton from '../../Buttons/RootButton'
import ErrorMessage from '../../ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '../../Identity/ConfirmModals/utils'
import { AtIcon } from '../../SpecialInputText'
import classes from './ConfirmUsername.module.css'

interface ConfirmPremiumUsernameSetupProps {
  close: () => void
  success: () => void
  address: string
  handle: string
  handleConfirmation: (confirmed: boolean) => void
  price: number
  isRenewal: boolean
}

const ConfirmPremiumUsername: React.FC<ConfirmPremiumUsernameSetupProps> = ({
  close,
  success,
  address,
  handle,
  handleConfirmation,
  price,
  isRenewal,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()

  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<JSX.Element | string>('')
  const [dnsErrorMessage, setDnsErrorMessage] = useState<JSX.Element | string>('')
  const [userIdentity, setUserIdentity] = useLocalStorage<UserIdentity>(`userIdentity-${address}`)

  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  const { confirmHandleAvailability } = useL1NS()

  // Normalize handle
  const normalizedHandle = normalize(handle)

  // DNS Rules check
  useEffect(() => {
    // check if handle complies with DNS rules
    setDnsErrorMessage('')
    const dnsRegex =
      /^(?=.{1,253}$)(?!-)[A-Za-z0-9-]{1,63}(?!\.)(?:\.(?!-)[A-Za-z0-9-]{1,63}(?!\.))*$/
    const isValid = dnsRegex.test(handle)
    if (!isValid) {
      setDnsErrorMessage(
        <Text style={{ color: theme.colors.notification[0] }}>
          <Trans i18nKey="components.identity.confirmModal.dnsError">
            This username may not be DNS compatible. This could limit the interoperability of your
            metaverse identity in the future. For more info, see{' '}
            <a
              href="https://docs.lamina1.com/docs/L1-Username-Guidelines--Recommendations"
              target="_blank"
              rel="noreferrer"
            >
              L1 Usernames Guidelines & Recommendations
            </a>
          </Trans>
        </Text>
      )
    }
  }, [handle])

  const contractCalls: ContractCall[] | undefined = useMemo(() => {
    if (!normalizedHandle || !price) {
      return undefined
    }
    return isRenewal
      ? [
          {
            args: {
              address: CONTRACT_COLLECTOR_ADDRESS as `0x${string}`,
              abi: L1PaymentCollector,
              functionName: 'renew',
              args: [normalizedHandle],
              value: parseEther(String(price)),
            },
          },
        ]
      : [
          {
            args: {
              address: CONTRACT_COLLECTOR_ADDRESS as `0x${string}`,
              abi: L1PaymentCollector,
              functionName: 'purchase',
              args: [normalizedHandle],
              value: parseEther(String(price)),
            },
          },
        ]
  }, [normalizedHandle, price, isRenewal])

  // Write to contract - purchase username
  const claimPaidHandle = useCallback(async () => {
    setErrorMessage('')
    if (
      !normalizedHandle ||
      !address ||
      !userIdentity ||
      !(contractCalls && contractCalls.length > 0)
    )
      return

    setIsLoading(true)
    handleConfirmation(true)
    try {
      // Write to contract
      await writeContract(contractCalls[0].args)

      // Verify if username was purchased and already on ID subnet
      const checkPurchaseCompletion = setInterval(async () => {
        const available = await confirmHandleAvailability(normalizedHandle)
        if (!available) {
          setIsLoading(false)
          setIsReady(true)
          setIsError(false)
          clearInterval(checkPurchaseCompletion)
        }
      }, 1000)
      if (!isRenewal) {
        setUserIdentity({
          ...userIdentity,
          username: normalizedHandle,
        })
      }
    } catch (error: any) {
      setIsLoading(false)
      setIsReady(false)
      handleConfirmation(false)
      setIsError(true)
      setErrorMessage(error.message)
    }
  }, [normalizedHandle, address, isRenewal, userIdentity, contractCalls, writeContract])

  return (
    <Stack className={classes.identityComponent} style={{ gap: '20px' }}>
      {errorMessage && isLoading && (
        <ErrorMessage
          message={t(
            'components.identity.error.premiumUsername',
            'Error {{msg}} premium username',
            { msg: isRenewal ? 'renewing' : 'claiming' }
          )}
          details={errorMessage}
        />
      )}
      {isLoading && (
        <ConfirmationLoading title={t('components.identity.loadingId', 'L1 to ID Subnet')} />
      )}
      {isReady && (
        <ConfirmationReady
          message={t('components.identity.premiumUsernameClaimed', 'Username {{msg}}!', {
            msg: isRenewal ? 'renewed' : 'claimed',
          })}
          success={success}
        />
      )}
      {!isReady && !isLoading && (
        <>
          <Text>
            <Trans i18nKey="components.identity.confirmModal.bodyPaid">
              {{ msg: isRenewal ? 'Renewing' : 'Purchasing' }} this premium username costs{' '}
              {{ price }} L1. Once confirmed, this action is non-refundable.
            </Trans>
          </Text>
          <Stack className={classes.confirmUsername}>
            <Stack className={classes.identityDisplay}>
              <Text className={classes.atIcon}>
                <AtIcon />
              </Text>
              <Stack className={classes.identityDisplayTextStack}>
                <Text style={{ fontFamily: 'monument-grotesk-heavy, sans-serif', fontSize: 20 }}>
                  {normalizedHandle}
                </Text>
              </Stack>
            </Stack>
          </Stack>
          {dnsErrorMessage && (
            <Stack className={classes.dnsErrorMessageComponent} style={{ gap: '6px' }}>
              <IconExclamationCircle className={classes.dnsIcon} />
              <Text className={classes.dnsErrorMessage}>{dnsErrorMessage}</Text>
            </Stack>
          )}
          <Stack className={classes.identityButtonsStack} style={{ gap: '8px' }}>
            <RootButton expand secondary style1={false} onClick={close}>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            {isError ? (
              <RootButton
                expand
                secondary
                style1={false}
                onClick={claimPaidHandle}
                disabled={isLoading}
              >
                {t('components.identity.confirmModal.orderRetry', 'Try Again')}
              </RootButton>
            ) : (
              <RootButton expand style1={false} onClick={claimPaidHandle} disabled={isLoading}>
                {isRenewal
                  ? t('components.identity.confirmModal.orderRenew', 'Renew')
                  : t('components.identity.confirmModal.orderPurchase', 'Purchase')}
              </RootButton>
            )}
          </Stack>
          <TransactionSummary amount={`${price}`} isOrder contractCalls={contractCalls} />
        </>
      )}
    </Stack>
  )
}

export default ConfirmPremiumUsername
